import React, { useEffect, useRef } from 'react';
import { Text, View } from '../../../src/Components/Themed';
import { useColors } from '../../../src/constants/useColors';
import { Animated, StyleSheet, TouchableOpacity } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';

type FreeBonusBannerProps = {
    amount?: string,
    message?: string,
    ctaText?: string,
    onPress?: () => void,
}

const FreeBonusBanner = ({
    amount = '$10',
    message = 'FREE to place your first bet!',
    ctaText = 'Claim Now',
    onPress
}: FreeBonusBannerProps) => {
    const Colors = useColors();
    const pulseAnim = useRef(new Animated.Value(1)).current;
    const glowAnim = useRef(new Animated.Value(0)).current;

    useEffect(() => {
        // Pulse animation for the emoji
        const pulse = Animated.loop(
            Animated.sequence([
                Animated.timing(pulseAnim, {
                    toValue: 1.2,
                    duration: 1000,
                    useNativeDriver: true,
                }),
                Animated.timing(pulseAnim, {
                    toValue: 1,
                    duration: 1000,
                    useNativeDriver: true,
                }),
            ])
        );

        // Glow animation for the button
        const glow = Animated.loop(
            Animated.sequence([
                Animated.timing(glowAnim, {
                    toValue: 1,
                    duration: 1500,
                    useNativeDriver: false,
                }),
                Animated.timing(glowAnim, {
                    toValue: 0,
                    duration: 1500,
                    useNativeDriver: false,
                }),
            ])
        );

        pulse.start();
        glow.start();

        return () => {
            pulse.stop();
            glow.stop();
        };
    }, []);

    const buttonScale = glowAnim.interpolate({
        inputRange: [0, 1],
        outputRange: [1, 1.05],
    });

    return (
        <LinearGradient
            colors={['#10b981', '#059669', '#047857']}
            start={{ x: 0, y: 0 }}
            end={{ x: 1, y: 1 }}
            style={styles.banner}
        >
            <View transparent style={styles.content}>
                <View transparent style={styles.leftSection}>
                    <Animated.View style={[styles.iconContainer, { transform: [{ scale: pulseAnim }] }]}>
                        <Text style={styles.coinEmoji}>💰</Text>
                    </Animated.View>
                    <View transparent style={styles.textContainer}>
                        <Text theme='h1' size={14} color={Colors.text.white} style={styles.amountText}>
                            Get {amount}
                        </Text>
                        <Text theme='h2' size={11} color={Colors.text.white} style={styles.messageText}>
                            {message}
                        </Text>
                    </View>
                </View>
                {onPress && (
                    <Animated.View style={{ transform: [{ scale: buttonScale }] }}>
                        <TouchableOpacity
                            style={styles.ctaButton}
                            onPress={onPress}
                            activeOpacity={0.8}
                        >
                            <Text theme='h1' size={12} color={Colors.absolutes.brand.electric} style={styles.ctaText}>
                                {ctaText} →
                            </Text>
                        </TouchableOpacity>
                    </Animated.View>
                )}
            </View>
        </LinearGradient>
    );
};

const styles = StyleSheet.create({
    banner: {
        paddingVertical: 10,
        paddingHorizontal: 12,
        shadowColor: '#10b981',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.4,
        shadowRadius: 6,
        elevation: 5,
    },
    content: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
    },
    leftSection: {
        flexDirection: 'row',
        alignItems: 'center',
        flex: 1,
    },
    iconContainer: {
        marginRight: 10,
    },
    textContainer: {
        flex: 1,
    },
    amountText: {
        fontWeight: '800',
        letterSpacing: 0.5,
    },
    messageText: {
        marginTop: 2,
        opacity: 0.95,
    },
    ctaButton: {
        backgroundColor: 'rgba(255,255,255,0.95)',
        paddingHorizontal: 14,
        paddingVertical: 6,
        borderRadius: 20,
        marginLeft: 8,
    },
    ctaText: {
        fontWeight: '700',
        letterSpacing: 0.3,
    },
    coinEmoji: {
        fontSize: 20,
    },
});

export default FreeBonusBanner;
